import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { taxRules as taxApi, type TaxRule } from '../lib/api.js'
import {
  Button,
  Input,
  Select,
  Switch,
  Modal,
  Badge,
  Empty,
  Spinner,
  toast,
} from '../components/ui/index.js'
import { useI18n } from '../lib/i18n.js'

const TAX_CATEGORIES = [
  { value: 'food', key: 'admin.taxes.cat_food' },
  { value: 'beverage', key: 'admin.taxes.cat_beverage' },
  { value: 'tobacco', key: 'admin.taxes.cat_tobacco' },
  { value: 'other', key: 'admin.taxes.cat_other' },
]

export function TaxRules() {
  const { t } = useI18n()
  const qc = useQueryClient()
  const [modal, setModal] = useState<{ open: boolean; rule?: TaxRule }>({ open: false })

  const { data: rules = [], isLoading } = useQuery({
    queryKey: ['tax-rules'],
    queryFn: () => taxApi.list(),
  })

  if (isLoading)
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="w-8 h-8" />
      </div>
    )

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.taxes.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('admin.taxes.subtitle')}</p>
        </div>
        <Button size="sm" onClick={() => setModal({ open: true })}>
          {t('admin.taxes.new')}
        </Button>
      </div>

      {rules.length === 0 ? (
        <Empty
          title={t('admin.taxes.empty')}
          description={t('admin.taxes.empty_hint')}
          action={
            <Button size="sm" onClick={() => setModal({ open: true })}>
              {t('admin.taxes.create')}
            </Button>
          }
        />
      ) : (
        <div className="space-y-3">
          {rules.map((rule) => (
            <RuleCard
              key={rule.id}
              rule={rule}
              onEdit={() => setModal({ open: true, rule })}
              qc={qc}
            />
          ))}
        </div>
      )}

      <TaxRuleModal {...modal} onClose={() => setModal({ open: false })} qc={qc} />
    </div>
  )
}

function RuleCard({
  rule,
  onEdit,
  qc,
}: {
  rule: TaxRule
  onEdit: () => void
  qc: ReturnType<typeof useQueryClient>
}) {
  const { t } = useI18n()
  const toggleMut = useMutation({
    mutationFn: () => taxApi.update(rule.id, { is_active: !rule.is_active }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['tax-rules'] }),
    onError: (e: Error) => toast.error(e.message),
  })

  const catDef = TAX_CATEGORIES.find((c) => c.value === rule.tax_category)
  const catLabel = catDef ? t(catDef.key) : rule.tax_category

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center gap-4">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-0.5">
          <p className="font-medium text-gray-900">{rule.label}</p>
          <Badge color={rule.is_active ? 'green' : 'gray'}>
            {rule.is_active ? t('admin.taxes.active') : t('admin.taxes.inactive')}
          </Badge>
        </div>
        <p className="text-sm text-gray-500">
          {catLabel} · {(rule.rate_percent / 100).toFixed(2)}% ·{' '}
          {rule.is_inclusive ? t('admin.taxes.inclusive') : t('admin.taxes.exclusive')}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Switch
          checked={rule.is_active}
          onChange={() => toggleMut.mutate()}
          disabled={toggleMut.isPending}
        />
        <Button variant="ghost" size="sm" onClick={onEdit}>
          {t('admin.common.edit')}
        </Button>
      </div>
    </div>
  )
}

function TaxRuleModal({
  open,
  rule,
  onClose,
  qc,
}: {
  open: boolean
  rule?: TaxRule
  onClose: () => void
  qc: ReturnType<typeof useQueryClient>
}) {
  const { t } = useI18n()
  const [taxCat, setTaxCat] = useState<TaxRule['tax_category']>(rule?.tax_category ?? 'food')
  const [rateStr, setRateStr] = useState(rule ? String(rule.rate_percent / 100) : '')
  const [label, setLabel] = useState(rule?.label ?? '')
  const [inclusive, setInclusive] = useState(rule?.is_inclusive ?? true)

  const [prevOpen, setPrevOpen] = useState(false)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) {
      setTaxCat(rule?.tax_category ?? 'food')
      setRateStr(rule ? String(rule.rate_percent / 100) : '')
      setLabel(rule?.label ?? '')
      setInclusive(rule?.is_inclusive ?? true)
    }
  }

  const mut = useMutation({
    mutationFn: () => {
      const rate = Math.round(parseFloat(rateStr.replace(',', '.')) * 100)
      return rule
        ? taxApi.update(rule.id, { rate_percent: rate, label })
        : taxApi.create({
            tax_category: taxCat,
            rate_percent: rate,
            label,
            is_inclusive: inclusive,
          })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tax-rules'] })
      toast.success(rule ? t('admin.taxes.updated_toast') : t('admin.taxes.created_toast'))
      onClose()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={rule ? t('admin.taxes.edit_title') : t('admin.taxes.new_title')}
      size="sm"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t('admin.common.cancel')}
          </Button>
          <Button size="sm" loading={mut.isPending} onClick={() => mut.mutate()}>
            {rule ? t('admin.common.save') : t('admin.taxes.create_short')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!rule && (
          <Select
            label={t('admin.taxes.category')}
            value={taxCat}
            onChange={(e) => setTaxCat(e.target.value as TaxRule['tax_category'])}
            options={TAX_CATEGORIES.map((c) => ({ value: c.value, label: t(c.key) }))}
          />
        )}
        <Input
          label={t('admin.taxes.rate')}
          value={rateStr}
          onChange={(e) => setRateStr(e.target.value)}
          placeholder="13"
          hint={t('admin.taxes.rate_hint')}
        />
        <Input
          label={t('admin.taxes.label')}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={t('admin.taxes.label_ph')}
        />
        {!rule && (
          <Switch
            checked={inclusive}
            onChange={setInclusive}
            label={t('admin.taxes.inclusive_label')}
            description={t('admin.taxes.inclusive_hint')}
          />
        )}
      </div>
    </Modal>
  )
}
